/**
 * Inbox grouping and filters for the Ops-style Notifications page / bell.
 * Type chips are AND-combined with status + search.
 */

export type InboxStatus = 'unread' | 'all' | 'read';

export type InboxTypeGroup =
  | 'mentions'
  | 'comments'
  | 'files'
  | 'tasks'
  | 'billing'
  | 'leads'
  | 'messages'
  | 'hiring';

export const INBOX_TYPE_GROUPS: { id: InboxTypeGroup; label: string }[] = [
  { id: 'mentions', label: 'Mentions' },
  { id: 'comments', label: 'Comments' },
  { id: 'files', label: 'Files' },
  { id: 'tasks', label: 'Tasks' },
  { id: 'billing', label: 'Billing' },
  { id: 'leads', label: 'Leads' },
  { id: 'messages', label: 'Messages' },
  { id: 'hiring', label: 'Hiring' },
];

const MENTIONS = new Set(['mention', 'task_mention', 'board_mention']);
const COMMENTS = new Set([
  'task_comment',
  'architect_comment',
  'external_comment',
  'pdf_comment',
  'pdf_external_comment',
  'board_comment',
  'proposal_comment',
  'portal_feedback',
]);
const FILES = new Set(['project_file_version', 'plan_review_complete', 'portal_upload']);
const TASKS = new Set([
  'task_assigned',
  'lead_todo_assigned',
  'task_reminder',
  'proposal_ops_review_requested',
  'proposal_ops_review_approved',
  'onboarding_action_needed',
]);
const BILLING = new Set([
  'billing_payables',
  'contractor_invoice',
  'capital_plan',
  'card_expense_digest',
  'purchase_requisition',
  'supplier_rfq_upload',
  'designer_invoice_review',
  'qbo_bill_office',
]);
const LEADS = new Set([
  'new_lead',
  'lead_qualified',
  'deal_won',
  'portal_deliverable_approval',
  'questionnaire_completed',
]);
const MESSAGES = new Set(['new_message', 'team_chat']);
const HIRING = new Set(['job_application', 'crew_job_commit', 'contractor_onboard']);

export function notificationTypeGroup(type: string | undefined): InboxTypeGroup | null {
  const t = String(type || '');
  if (MENTIONS.has(t)) return 'mentions';
  if (COMMENTS.has(t)) return 'comments';
  if (FILES.has(t)) return 'files';
  if (TASKS.has(t)) return 'tasks';
  if (BILLING.has(t)) return 'billing';
  if (LEADS.has(t)) return 'leads';
  if (MESSAGES.has(t)) return 'messages';
  if (HIRING.has(t)) return 'hiring';
  return null;
}

/** Short label for the row type column (uppercase in CSS). */
export function inboxTypeLabel(type: string | undefined): string {
  switch (String(type || '')) {
    case 'new_lead':
      return 'Lead';
    case 'lead_qualified':
      return 'Qualified';
    case 'deal_won':
      return 'Deal';
    case 'new_message':
      return 'Message';
    case 'team_chat':
      return 'Chat';
    case 'mention':
    case 'task_mention':
    case 'board_mention':
      return 'Mention';
    case 'board_comment':
      return 'Board';
    case 'task_comment':
    case 'proposal_comment':
    case 'portal_feedback':
      return 'Comment';
    case 'pdf_comment':
    case 'pdf_external_comment':
      return 'Comment';
    case 'architect_comment':
    case 'architect_standup_report':
      return 'Designer';
    case 'external_comment':
      return 'External';
    case 'task_assigned':
      return 'Task';
    case 'lead_todo_assigned':
      return 'Todo';
    case 'task_reminder':
      return 'Reminder';
    case 'project_file_version':
      return 'Files';
    case 'plan_review_complete':
      return 'Review';
    case 'portal_upload':
      return 'Upload';
    case 'supplier_rfq_upload':
      return 'RFQ';
    case 'contractor_invoice':
    case 'billing_payables':
      return 'Billing';
    case 'capital_plan':
      return 'Capital';
    case 'card_expense_digest':
      return 'Cards';
    case 'purchase_requisition':
      return 'PO';
    case 'designer_invoice_review':
      return 'Invoice';
    case 'qbo_bill_office':
      return 'Bill';
    case 'bug_squashed':
      return 'Bug';
    case 'job_application':
      return 'Hiring';
    case 'crew_job_commit':
      return 'Crew';
    case 'contractor_onboard':
      return '1099';
    case 'proposal_ops_review_requested':
    case 'proposal_ops_review_approved':
      return 'OPS';
    case 'portal_deliverable_approval':
      return 'Portal';
    default:
      return 'Note';
  }
}

export interface InboxFilterable {
  type?: string;
  title?: string;
  message?: string;
  read?: boolean;
}

export function matchesInboxFilters(
  n: InboxFilterable,
  opts: { status: InboxStatus; typeGroup: InboxTypeGroup | null; search: string },
): boolean {
  if (opts.status === 'unread' && n.read) return false;
  if (opts.status === 'read' && !n.read) return false;
  if (opts.typeGroup && notificationTypeGroup(n.type) !== opts.typeGroup) return false;
  const q = opts.search.trim().toLowerCase();
  if (q) {
    const hay = `${n.title || ''} ${n.message || ''}`.toLowerCase();
    if (!hay.includes(q)) return false;
  }
  return true;
}

export function presentInboxTypeGroups<T extends InboxFilterable>(items: T[]): InboxTypeGroup[] {
  const present = new Set<InboxTypeGroup>();
  for (const n of items) {
    const g = notificationTypeGroup(n.type);
    if (g) present.add(g);
  }
  return INBOX_TYPE_GROUPS.map((g) => g.id).filter((id) => present.has(id));
}

export function inboxStatusCounts<T extends InboxFilterable>(items: T[]): {
  all: number;
  unread: number;
  read: number;
} {
  let unread = 0;
  let read = 0;
  for (const n of items) {
    if (n.read) read += 1;
    else unread += 1;
  }
  return { all: items.length, unread, read };
}

/** Compact relative time for 40px rows (e.g. 12m, 12h, 3d). */
export function compactRelativeTime(date: Date, now: Date = new Date()): string {
  const ms = now.getTime() - date.getTime();
  const mins = Math.max(0, Math.round(ms / 60000));
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.round(hours / 24);
  if (days < 14) return `${days}d`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function inboxDateGroupLabel(date: Date, now: Date = new Date()): string {
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startThat = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.round((startToday.getTime() - startThat.getTime()) / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

export function groupInboxByDate<T extends { createdAt?: Date; timestamp?: Date }>(
  items: T[],
  now: Date = new Date(),
): { label: string; items: T[] }[] {
  const groups: { label: string; items: T[] }[] = [];
  let current = '';
  for (const n of items) {
    const at = n.createdAt ?? n.timestamp;
    if (!at) continue;
    const label = inboxDateGroupLabel(at, now);
    if (label !== current) {
      groups.push({ label, items: [] });
      current = label;
    }
    groups[groups.length - 1].items.push(n);
  }
  return groups;
}
